/**
 * Admin screen for the GLOBAL default game-centre branding — name + logo
 * shown on every device that doesn't GPS-match a venue with its own
 * override (see `src/ui/views/venues.ts`'s per-venue branding fields).
 *
 * Saved to the relay (KV), not just this device's `localStorage`, so any
 * device that activates picks it up as its default — see `readBranding`'s
 * doc comment for the bug this fixes (only the admin's own device used to
 * ever see real branding). Same admin-secret gate as Venues/Access Control.
 */

import { readLogoFile } from '../logoImage';
import { AdminAuthError, fetchGlobalBranding, getStoredAdminSecret, saveGlobalBrandingApi, storeAdminSecret } from '../adminApi';
import {
  DEFAULT_BRANDING,
  DEFAULT_WINNER_MESSAGE,
  readBranding,
  renderBrandHeader,
  writeBranding,
  type Branding,
} from '../branding';
import { esc } from '../helpers';

export interface BrandingScreenActions {
  /** Called after a successful save. */
  exit(): void;
  cancel(): void;
}

const MAX_LOGO_BYTES = 1_000_000; // 1MB — plenty for a logo, small enough for a KV value.

export function renderBrandingScreen(root: HTMLElement, actions: BrandingScreenActions): void {
  if (!getStoredAdminSecret()) {
    renderSecretPrompt(root, actions, undefined);
    return;
  }
  void loadAndRender(root, actions);
}

function renderSecretPrompt(root: HTMLElement, actions: BrandingScreenActions, error: string | undefined): void {
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Admin Secret Required</h1>
      <p class="muted">
        Same secret as Access Control / Venues — it's remembered on this device once entered.
      </p>

      ${error ? `<div class="error-box">${esc(error)}</div>` : ''}

      <label for="secretInput">Admin secret</label>
      <input type="password" id="secretInput" autocomplete="off" />

      <div class="row" style="margin-top:1rem">
        <button type="button" id="backBtn" class="secondary">Back</button>
        <button type="button" id="saveBtn">Continue</button>
      </div>
    </div>
  `;

  root.querySelector<HTMLButtonElement>('#backBtn')!.addEventListener('click', () => actions.cancel());
  root.querySelector<HTMLButtonElement>('#saveBtn')!.addEventListener('click', () => {
    const secret = root.querySelector<HTMLInputElement>('#secretInput')!.value.trim();
    if (!secret) {
      renderSecretPrompt(root, actions, 'Enter the admin secret.');
      return;
    }
    storeAdminSecret(secret);
    void loadAndRender(root, actions);
  });
}

async function loadAndRender(root: HTMLElement, actions: BrandingScreenActions): Promise<void> {
  root.innerHTML = `${renderBrandHeader()}<div class="card"><h1>Game Centre Branding</h1><p class="muted">Loading…</p></div>`;
  try {
    const branding = (await fetchGlobalBranding()) ?? readBranding();
    renderForm(root, actions, branding, undefined);
  } catch (e) {
    if (e instanceof AdminAuthError) {
      renderSecretPrompt(root, actions, 'Incorrect admin secret.');
      return;
    }
    renderForm(root, actions, readBranding(), 'Could not load the saved branding. Check your connection.');
  }
}

function renderForm(
  root: HTMLElement,
  actions: BrandingScreenActions,
  current: Branding,
  error: string | undefined,
): void {
  const preview = current.logoDataUrl
    ? `<img src="${current.logoDataUrl}" alt="" class="brand-logo-preview" />`
    : `<span class="muted">No logo set — a placeholder tile is shown instead.</span>`;

  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Game Centre Branding</h1>
      <p class="muted">
        The default shown on every device — a specific venue's own branding (set in Venues) overrides
        this for devices that GPS-match it.
      </p>

      ${error ? `<div class="error-box">${esc(error)}</div>` : ''}

      <label for="centreName">Name</label>
      <input type="text" id="centreName" value="${esc(current.name)}" />

      <label>Logo</label>
      <div style="margin-bottom:0.5rem">${preview}</div>
      <input type="file" id="logoFile" accept="image/*" />
      ${
        current.logoDataUrl
          ? `<button type="button" id="removeLogoBtn" class="ghost" style="margin-top:0.5rem">Remove logo</button>`
          : ''
      }

      <label for="winnerMessage" style="margin-top:1rem;display:block">Winner banner message</label>
      <p class="muted" style="margin:0 0 0.4rem">
        Shown as a scrolling banner on Final Results. Write your own remark — use <code>{winner}</code>
        anywhere you want the winner's name inserted (skip it for a message with no name at all).
      </p>
      <textarea id="winnerMessage" rows="2" placeholder="${esc(DEFAULT_WINNER_MESSAGE)}">${esc(current.winnerMessage ?? '')}</textarea>

      <div class="row" style="margin-top:1rem">
        <button id="resetBtn" class="ghost" type="button">Reset to defaults</button>
        <button id="cancelBtn" class="secondary" type="button">Cancel</button>
        <button id="saveBtn" type="button">Save</button>
      </div>
    </div>
  `;

  const currentName = () => root.querySelector<HTMLInputElement>('#centreName')!.value;
  const currentWinnerMessage = () => root.querySelector<HTMLTextAreaElement>('#winnerMessage')!.value;

  root.querySelector<HTMLInputElement>('#logoFile')!.addEventListener('change', (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;

    if (file.size > MAX_LOGO_BYTES) {
      renderForm(
        root,
        actions,
        { name: currentName(), logoDataUrl: current.logoDataUrl, winnerMessage: currentWinnerMessage() },
        `That image is ${Math.round(file.size / 1024)} KB — please use one under ${MAX_LOGO_BYTES / 1_000_000}MB.`,
      );
      return;
    }

    void readLogoFile(file).then((logoDataUrl) => {
      renderForm(root, actions, { name: currentName(), logoDataUrl, winnerMessage: currentWinnerMessage() }, undefined);
    });
  });

  root.querySelector<HTMLButtonElement>('#removeLogoBtn')?.addEventListener('click', () => {
    renderForm(
      root,
      actions,
      { name: currentName(), logoDataUrl: undefined, winnerMessage: currentWinnerMessage() },
      undefined,
    );
  });

  root.querySelector<HTMLButtonElement>('#resetBtn')!.addEventListener('click', () => {
    renderForm(root, actions, { ...DEFAULT_BRANDING }, undefined);
  });

  root.querySelector<HTMLButtonElement>('#cancelBtn')!.addEventListener('click', () => {
    actions.cancel();
  });

  root.querySelector<HTMLButtonElement>('#saveBtn')!.addEventListener('click', () => {
    const name = currentName().trim();
    if (!name) {
      renderForm(
        root,
        actions,
        { name: '', logoDataUrl: current.logoDataUrl, winnerMessage: currentWinnerMessage() },
        'Enter a name for your game centre.',
      );
      return;
    }
    const winnerMessage = currentWinnerMessage().trim() || undefined;
    const draft: Branding = { name, logoDataUrl: current.logoDataUrl, ...(winnerMessage ? { winnerMessage } : {}) };
    void (async () => {
      try {
        const saved = await saveGlobalBrandingApi(draft);
        // Keeps THIS device's own cached copy in sync too, so it reflects
        // the change immediately rather than waiting for the next
        // activate/verify round-trip to pull it back down.
        writeBranding(saved);
        actions.exit();
      } catch (e) {
        if (e instanceof AdminAuthError) {
          renderSecretPrompt(root, actions, 'Incorrect admin secret.');
          return;
        }
        renderForm(root, actions, draft, e instanceof Error ? e.message : 'Could not save branding.');
      }
    })();
  });
}
